/**
 * Home carousel: one horizontal list, tabs jump to category, scroll updates tab.
 * Catalog JSON is used because Liquid collection.products caps at 50.
 */
class ZgHomeCarousel extends HTMLElement {
  connectedCallback() {
    this.scroller = this.querySelector('[data-zg-scroller]');
    this.tabs = [...this.querySelectorAll('[data-zg-tab]')];
    this.seeAll = this.querySelector('[data-zg-see-all]');
    this.prevBtn = this.querySelector('[data-zg-prev]');
    this.nextBtn = this.querySelector('[data-zg-next]');
    this.status = this.querySelector('[data-zg-status]');
    this.ignoreUntil = 0;
    this.currency = this.dataset.currency || 'COP';
    this.locale = this.dataset.locale || 'es-CO';

    const raw = this.querySelector('[data-zg-config]');
    this.categories = [];

    try {
      this.categories = raw ? JSON.parse(raw.textContent || '[]') : [];
    } catch (error) {
      console.error('ZG carousel: config inválido', error);
    }

    this.prevBtn?.addEventListener('click', () => this.scrollByCards(-1));
    this.nextBtn?.addEventListener('click', () => this.scrollByCards(1));

    for (const tab of this.tabs) {
      tab.addEventListener('click', () => this.goToCategory(tab.dataset.zgTab || ''));
    }

    this.scroller?.addEventListener('scroll', () => this.onScroll(), { passive: true });
    window.addEventListener('resize', () => this.updateEdges());

    this.load();
  }

  async load() {
    const items = [];

    for (const category of this.categories) {
      if (!category.handle) continue;

      const products = await this.fetchCollection(category.handle);
      const picked = category.pick === 'best_price'
        ? this.pickBestPrice(products, category.limit)
        : this.pickBestType(products, category.limit);

      for (const product of picked) {
        items.push({ categoryId: category.id, product });
      }
    }

    this.render(items);
    this.setActive(this.categories[0]?.id || '');
    this.updateEdges();
  }

  async fetchCollection(handle) {
    const all = [];

    for (let page = 1; page <= 8; page += 1) {
      try {
        const response = await fetch(`/collections/${handle}/products.json?limit=50&page=${page}`);
        if (!response.ok) break;
        const data = await response.json();
        const batch = Array.isArray(data.products) ? data.products : [];
        all.push(...batch);
        if (batch.length < 50) break;
      } catch (error) {
        console.error('ZG carousel: no se pudo leer', handle, error);
        break;
      }
    }

    return all;
  }

  availablePrice(product) {
    const variants = (product.variants || []).filter((variant) => variant.available);
    if (!variants.length) return null;

    let max = 0;
    let compare = 0;

    for (const variant of variants) {
      const price = Number(variant.price) || 0;
      if (price > max) {
        max = price;
        compare = Number(variant.compare_at_price) || 0;
      }
    }

    return { price: max, compare };
  }

  pickBestType(products, limit) {
    const byType = new Map();

    for (const product of products) {
      const money = this.availablePrice(product);
      if (!money) continue;

      const key = (product.product_type || product.handle || '').toLowerCase();
      const prev = byType.get(key);
      if (!prev || money.price > prev.money.price) {
        byType.set(key, { product, money });
      }
    }

    return [...byType.values()]
      .sort((a, b) => b.money.price - a.money.price)
      .slice(0, limit)
      .map((row) => row.product);
  }

  pickBestPrice(products, limit) {
    return products
      .map((product) => {
        const money = this.availablePrice(product);
        return money ? { product, money } : null;
      })
      .filter(Boolean)
      .sort((a, b) => b.money.price - a.money.price)
      .slice(0, limit)
      .map((row) => row.product);
  }

  formatMoney(amount) {
    return new Intl.NumberFormat(this.locale, {
      style: 'currency',
      currency: this.currency,
      maximumFractionDigits: 0,
    }).format(Number(amount) || 0);
  }

  render(items) {
    if (!this.scroller) return;

    if (!items.length) {
      this.scroller.innerHTML = '';
      if (this.status) this.status.textContent = 'No hay productos en stock para mostrar.';
      return;
    }

    const html = items.map((item) => {
      const product = item.product;
      const money = this.availablePrice(product) || { price: 0, compare: 0 };
      const image = product.featured_image || product.images?.[0]?.src || '';
      const onSale = money.compare > money.price;
      const href = `/products/${product.handle}`;
      const title = this.escape(product.title || '');

      return `
        <li class="zg-car__item" data-category="${this.escape(item.categoryId)}" data-zg-card>
          <article class="zg-car__card">
            <a class="zg-car__hit" href="${href}">
              <span class="visually-hidden">${title}</span>
            </a>
            <div class="zg-car__media">
              ${onSale ? '<span class="zg-car__badge">Oferta</span>' : ''}
              ${image
                ? `<img class="zg-car__img" src="${this.escape(image)}" alt="${title}" width="600" height="600" loading="lazy">`
                : '<div class="zg-car__ph" aria-hidden="true"></div>'}
              <a class="zg-car__buy" href="${href}">Comprar</a>
            </div>
            <h3 class="zg-car__title">${title}</h3>
            <p class="zg-car__price">
              <span class="zg-car__now">${this.formatMoney(money.price)}</span>
              ${onSale ? `<s class="zg-car__was">${this.formatMoney(money.compare)}</s>` : ''}
            </p>
          </article>
        </li>
      `;
    }).join('');

    this.scroller.innerHTML = html;
    if (this.status) {
      this.status.textContent = '';
      this.status.hidden = true;
    }
  }

  escape(value) {
    return String(value)
      .replaceAll('&', '&amp;')
      .replaceAll('<', '&lt;')
      .replaceAll('>', '&gt;')
      .replaceAll('"', '&quot;');
  }

  cards() {
    return [...(this.scroller?.querySelectorAll('[data-zg-card]') || [])];
  }

  goToCategory(categoryId) {
    const card = this.cards().find((node) => node.dataset.category === categoryId);
    if (!card || !this.scroller) return;

    this.ignoreUntil = Date.now() + 450;
    this.setActive(categoryId);
    this.scroller.scrollTo({ left: card.offsetLeft, behavior: 'smooth' });
  }

  scrollByCards(direction) {
    const first = this.cards()[0];
    if (!first || !this.scroller) return;
    const styles = window.getComputedStyle(this.scroller);
    const gap = Number.parseFloat(styles.columnGap || styles.gap) || 24;
    this.scroller.scrollBy({ left: direction * (first.offsetWidth + gap), behavior: 'smooth' });
  }

  onScroll() {
    this.updateEdges();
    if (Date.now() < this.ignoreUntil || !this.scroller) return;

    const marker = this.scroller.scrollLeft + this.scroller.clientWidth * 0.28;
    let current = this.categories[0]?.id || '';

    for (const card of this.cards()) {
      if (card.offsetLeft <= marker) {
        current = card.dataset.category || current;
      } else {
        break;
      }
    }

    this.setActive(current);
  }

  setActive(categoryId) {
    for (const tab of this.tabs) {
      const on = tab.dataset.zgTab === categoryId;
      tab.classList.toggle('is-active', on);
      if (on) {
        tab.setAttribute('aria-current', 'true');
      } else {
        tab.removeAttribute('aria-current');
      }
    }

    const category = this.categories.find((item) => item.id === categoryId) || this.categories[0];
    if (this.seeAll && category) {
      this.seeAll.textContent = category.seeAll || 'Ver colección';
      this.seeAll.setAttribute('href', category.url || '#');
    }
  }

  updateEdges() {
    if (!this.scroller) return;
    const max = this.scroller.scrollWidth - this.scroller.clientWidth;
    const atStart = this.scroller.scrollLeft <= 8;
    const atEnd = this.scroller.scrollLeft >= max - 8 || max <= 0;

    this.toggleDisabled(this.prevBtn, atStart);
    this.toggleDisabled(this.nextBtn, atEnd);
  }

  toggleDisabled(button, disabled) {
    if (!button) return;
    button.disabled = disabled;
  }
}

if (!customElements.get('zg-home-carousel')) {
  customElements.define('zg-home-carousel', ZgHomeCarousel);
}
